import { useQuery, useQueryClient, type Query } from '@tanstack/react-query';
import type { DownloadUrlResponse } from '../../../../contracts';
import { DOCUMENT_DOWNLOAD_URL_KEY } from '../../../../contracts';
import { getDocumentDownloadUrl } from '../api';

/** Poll interval while waiting for a processing document's download URL. */
export const DOCUMENT_DOWNLOAD_READY_POLL_MS = 2_000;

/** Max completed fetch attempts before giving up on a not-yet-ready URL. */
export const DOCUMENT_DOWNLOAD_MAX_READY_POLLS = 30;

export type UseDocumentDownloadUrlOptions = {
  /**
   * When true, refetch until a download URL is present or
   * {@link DOCUMENT_DOWNLOAD_MAX_READY_POLLS} attempts complete.
   */
  pollUntilReady?: boolean;
};

/**
 * Interval helper for ready-polling. Stops when a URL lands or the attempt
 * budget is exhausted. Continues through query errors so a processing 404
 * can resolve on a later tick.
 */
export function getDocumentDownloadRefetchInterval(
  data: DownloadUrlResponse | undefined,
  dataUpdateCount: number,
  errorUpdateCount: number
): number | false {
  if (data?.url) return false;
  if (dataUpdateCount + errorUpdateCount >= DOCUMENT_DOWNLOAD_MAX_READY_POLLS) {
    return false;
  }
  return DOCUMENT_DOWNLOAD_READY_POLL_MS;
}

/**
 * Query hook to get a pre-signed download URL for a document.
 *
 * The URL expires in 60 minutes, so we use a staleTime of 50 minutes
 * to ensure we always have a valid URL (unless ready-polling).
 *
 * @param documentId - The ID of the document to get a download URL for (null to disable)
 * @param options.pollUntilReady - Poll until URL is ready or max attempts reached
 */
export function useDocumentDownloadUrl(
  documentId: string | null,
  options?: UseDocumentDownloadUrlOptions
) {
  const pollUntilReady = options?.pollUntilReady ?? false;
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [DOCUMENT_DOWNLOAD_URL_KEY, documentId],
    queryFn: () => getDocumentDownloadUrl(documentId!),
    enabled: !!documentId,
    // URL expires in 60 minutes, use 50 minutes staleTime for safety.
    // Ready-polling needs fresh responses each tick.
    staleTime: pollUntilReady ? 0 : 50 * 60 * 1000,
    ...(pollUntilReady
      ? {
          refetchInterval: (q: Query<DownloadUrlResponse, Error>) =>
            getDocumentDownloadRefetchInterval(
              q.state.data,
              q.state.dataUpdateCount,
              q.state.errorUpdateCount
            ),
          refetchIntervalInBackground: false,
          // Polling replaces per-request retries while waiting for readiness.
          retry: false as const,
        }
      : {}),
  });

  const queryState = queryClient.getQueryState([DOCUMENT_DOWNLOAD_URL_KEY, documentId]);
  const attempts =
    (queryState?.dataUpdateCount ?? 0) + (queryState?.errorUpdateCount ?? 0);
  const isWaitingForReady =
    pollUntilReady &&
    !!documentId &&
    !query.data?.url &&
    (query.isLoading || query.isFetching || attempts < DOCUMENT_DOWNLOAD_MAX_READY_POLLS);

  // eslint-disable-next-line @tanstack/query/no-rest-destructuring -- augment UseQueryResult with isWaitingForReady for callers
  return { ...query, isWaitingForReady };
}
